import http from '../../../services/api'
import type { APIResponse, Filters, APIPaginatedResponse, Pagination } from '../../../services/types'
import { getRequestParamString } from '../../../services/utils/utils'
import { ITEM_GROUP } from '../../../constants/ItemGroup'
import type { InventoryItemType } from '../types'

const apiPath = import.meta.env.VITE_API_PATH

const PRODUCT_ITEM_GROUP_ID = ITEM_GROUP.find((g) => g.name === 'Product')?.value

export type ItemTypeListOptions = {
  /** When true (default), only product item types. When false, returns types across all item groups. */
  productGroupOnly?: boolean
}

function itemGroupParamString(paramString: string, productGroupOnly: boolean): string {
  if (!productGroupOnly || PRODUCT_ITEM_GROUP_ID == null) {
    return ''
  }
  return `${paramString ? '&' : '?'}item_group_id=${PRODUCT_ITEM_GROUP_ID}`
}

async function getItemTypes(
  filters?: Partial<Filters>,
  pagination?: Partial<Pagination>,
  options?: ItemTypeListOptions,
  itemCategoryUuid?: string,
) {
  const paramString = getRequestParamString(filters, pagination)
  const productGroupOnly = options?.productGroupOnly !== false
  const itemGroupParam = itemGroupParamString(paramString, productGroupOnly)
  const categoryParam = itemCategoryUuid
    ? `${paramString || itemGroupParam ? '&' : '?'}item_category_uuid=${encodeURIComponent(itemCategoryUuid)}`
    : ''
  return await http.get<APIPaginatedResponse<InventoryItemType>>(
    `${apiPath}items/item-types${paramString}${itemGroupParam}${categoryParam}`,
  )
}

async function deleteItemType(itemTypeUuid: string) {
  return await http.delete<APIResponse<boolean>>(`${apiPath}items/item-types/${itemTypeUuid}`)
}

async function createItemType(input: InventoryItemType) {
  const payLoad = {
    name: input.name,
    item_category_uuid: input.item_category_uuid,
    description: input.description,
  }

  return await http.post<APIResponse<InventoryItemType>>(`${apiPath}items/item-types`, payLoad)
}

async function updateItemType(input: InventoryItemType) {
  const payLoad = {
    name: input.name,
    item_category_uuid: input.item_category_uuid,
    description: input.description,
  }

  return await http.patch<APIResponse<InventoryItemType>>(`${apiPath}items/item-types/${input.uuid}`, payLoad)
}

async function getItemTypesOfItemCategory(itemCategoryUuid: string) {
  return await http.get<APIResponse<InventoryItemType>>(`${apiPath}items/item-types/item-category/${itemCategoryUuid}`)
}

const itemTypeService = {
  getItemTypes,
  deleteItemType,
  createItemType,
  updateItemType,
  getItemTypesOfItemCategory,
}

export default itemTypeService
